import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useSidebarStore } from '../store/sidebarStore';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Briefcase,
  Package,
  Settings,
  LogOut,
  Sun,
  Moon,
  X,
  Users,
  ShieldCheck,
  Megaphone,
  MessageSquare,
  LifeBuoy,
  Play,
  UserPlus,
  Clock,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  UserCog,
  FileText,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Logo } from './Logo';
import { UnreadBadge } from './UnreadBadge';
import { supportApi } from '../lib/support';
import { cn } from '../lib/utils';
import { API_URL } from '../lib/api';
import { demoTimeLabel } from './demo/useDemoSession';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/equipment', icon: Package, label: 'Equipment' },
  { to: '/clients', icon: Users, label: 'Clients' },
  { to: '/crew', icon: UserCog, label: 'Crew / Suppliers' }, // TODO: Gate with "projects" plan in Phase 8
  // Treatments live in the Briefs section, at /treatments/:id
  { to: '/briefs', icon: FileText, label: 'Briefs', alsoActiveOn: ['/treatments'] },
  { to: '/jobs', icon: Briefcase, label: 'Jobs' },
];

export function Sidebar({ demo }) {
  const { user, logout, checkAdminStatus, isAuthenticated, isLoading, isDemo, checkDemoStatus, isAdmin, adminStatusChecked, subscription, checkSubscriptionStatus, subscriptionChecked, hasProjectsPlan, projectsPlanChecked, checkProjectsPlanAccess } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const { collapsed, toggleCollapsed, drawerOpen: isOpen, setDrawerOpen } = useSidebarStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [supportNeedsReply, setSupportNeedsReply] = useState(0);
  const [helpUnread, setHelpUnread] = useState(0);

  // Check admin status only once after authentication (uses cached value from store)
  useEffect(() => {
    if (isAuthenticated && !isLoading && user && !adminStatusChecked) {
      checkAdminStatus(); // This now caches the result in the store
    }
  }, [isAuthenticated, isLoading, user, adminStatusChecked, checkAdminStatus]);

  // Check Projects plan access
  useEffect(() => {
    if (isAuthenticated && !isLoading && user && !projectsPlanChecked) {
      checkProjectsPlanAccess();
    }
  }, [isAuthenticated, isLoading, user, projectsPlanChecked, checkProjectsPlanAccess]);

  // Check demo status on mount
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      checkDemoStatus();
    }
  }, [isAuthenticated, isLoading, checkDemoStatus]);

  // Check subscription status on mount (for non-demo users)
  useEffect(() => {
    if (isAuthenticated && !isLoading && !isDemo && !subscriptionChecked) {
      checkSubscriptionStatus();
    }
  }, [isAuthenticated, isLoading, isDemo, subscriptionChecked, checkSubscriptionStatus]);

  // Admins: conversations waiting on us
  useEffect(() => {
    if (!isAdmin) return undefined;
    const load = () => supportApi.counts().then((c) => setSupportNeedsReply(c.needs_reply)).catch(() => {});
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Everyone but the demo: replies they haven't read yet. Re-checked on
  // every page change, so reading a reply clears the badge straight away.
  useEffect(() => {
    if (!isAuthenticated || isDemo) return undefined;
    const load = () => supportApi.myUnread().then(setHelpUnread).catch(() => {});
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isDemo, pathname]);

  const handleLogout = async () => {
    if (isDemo) {
      closeDrawer();
      await demo.endDemo('exited');
      return;
    }
    await logout();
    navigate('/');
  };

  // Collapsing is a desktop thing: every rail class is lg:-prefixed, so the
  // phone drawer always opens at full width with its labels.
  const rail = collapsed;
  const closeDrawer = () => setDrawerOpen(false);
  const toBilling = () => {
    closeDrawer();
    navigate('/settings?tab=billing');
  };

  return (
    <>
      {/* The button that opens this on a phone or tablet is MenuButton, which
          Layout puts beside each page's title. */}

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeDrawer}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-background border-r border-border flex flex-col transition-[transform,width] duration-300 lg:translate-x-0 max-lg:shadow-modal',
          rail && 'lg:w-16',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* The tab that collapses and expands it, halfway down its right edge */}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!rail}
          title={rail ? 'Expand' : 'Collapse'}
          className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 h-12 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
          data-testid="sidebar-collapse-tab"
        >
          {rail ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        {/* Logo */}
        {/* 80px tall, so the logo shares a centre line with each page's sticky toolbar (PageToolbar) */}
        <div className={cn('flex items-center gap-3 px-6 h-20 shrink-0', rail && 'lg:justify-center lg:px-0')}>
          <div className="w-8 h-8 shrink-0 flex items-center justify-center">
            <Logo className="w-8 h-8" alt="" />
          </div>
          <div className={cn('flex-1', rail && 'lg:hidden')}>
            <p className="text-lg font-medium whitespace-nowrap">No Half Days</p>
          </div>
          {/* Closes the drawer; on a desktop the sidebar is always there */}
          <Button variant="ghost" size="icon" className="lg:hidden shrink-0 text-muted-foreground" onClick={closeDrawer} aria-label="Close menu">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        {/* Scrolls up and down only: on the 63px rail a sideways scrollbar would show under the links */}
        <nav className={cn('flex-1 px-3 py-2 space-y-1 overflow-y-auto overflow-x-hidden', rail && 'lg:px-2')}>
          {navItems
            .filter(item => {
              // Filter out Crew if user doesn't have Projects plan access
              if (item.to === '/crew') {
                return isAdmin;
              }
              // Briefs is gated the same way as Crew (Projects plan → admin for now)
              if (item.to === '/briefs') {
                return isAdmin;
              }
              return true;
            })
            .map((item) => (
              <SideLink
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                alsoActiveOn={item.alsoActiveOn}
                rail={rail}
                onClick={closeDrawer}
                testId={`nav-${item.label.toLowerCase()}`}
              />
            ))}
          
          {/* Settings with separator */}
          {/* !mt: the nav's space-y would otherwise override it; 8px each side of the line */}
          <div className="!mt-2 pt-2 border-t border-border">
            <SideLink to="/settings" icon={Settings} label="Settings" rail={rail} onClick={closeDrawer} testId="nav-settings" />
          </div>
          
          {/* Help: shown in the demo too, where its page explains it needs an account */}
          <SideLink to="/help" icon={LifeBuoy} label="Help" rail={rail} onClick={closeDrawer} badge={helpUnread} testId="nav-help" />
        </nav>

        {/* User & Actions */}
        <div className={cn('p-4 border-t border-border space-y-4', rail && 'lg:px-2.5 lg:py-3')}>
          {/* Theme Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={toggleTheme}
            className={cn('w-full justify-start gap-3', rail && 'lg:justify-center lg:px-0')}
            title={rail ? (theme === 'dark' ? 'Light Mode' : 'Dark Mode') : undefined}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span className={cn(rail && 'lg:sr-only')}>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </Button>

          {/* User Info */}
          <div className={cn('flex items-center gap-3 px-1', rail && 'lg:justify-center lg:px-0')} title={rail ? `${user?.name || ''} · ${user?.email || ''}` : undefined}>
            <div className="w-8 h-8 shrink-0 rounded-full bg-surface flex items-center justify-center text-sm font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className={cn('flex-1 min-w-0', rail && 'lg:hidden')}>
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>

          {/* Admin Links */}
          {isAdmin && (
            <div className="space-y-1">
              <SideLink to="/users" icon={ShieldCheck} label="Users" small rail={rail} onClick={closeDrawer} testId="nav-users" />
              <SideLink to="/announcements" icon={Megaphone} label="Announcements" small rail={rail} onClick={closeDrawer} />
              <SideLink to="/support" icon={MessageSquare} label="Support" small rail={rail} onClick={closeDrawer} badge={supportNeedsReply} testId="nav-support" />
            </div>
          )}

          {/* Demo Mode Banner */}
          {isDemo && (
            <>
              <div className={cn('bg-ochre-soft rounded-cards p-3 space-y-2', rail && 'lg:hidden')}>
                <div className="flex items-center gap-2 text-ochre">
                  <Play className="w-4 h-4" />
                  <span className="text-sm font-medium">Demo mode</span>
                </div>
                {demo.minutesLeft !== null && (
                  <p className="text-sm text-muted-foreground">{demoTimeLabel(demo.minutesLeft)}</p>
                )}
                <button
                  onClick={() => {
                    closeDrawer();
                    demo.signUp('sidebar');
                  }}
                  className="flex items-center gap-2 text-sm text-clay underline underline-offset-4 hover:text-foreground"
                >
                  <UserPlus className="w-3 h-3" />
                  Create your own account
                </button>
              </div>
              {rail && (
                <RailBadge
                  icon={Play}
                  tone="amber"
                  label={demo.minutesLeft !== null ? `Demo mode · ${demoTimeLabel(demo.minutesLeft)}` : 'Demo mode'}
                />
              )}
            </>
          )}

          {/* Subscription Status Banner - shown for non-demo users */}
          {!isDemo && subscription && subscription.plan !== 'demo' && (
            <>
              {/* Trial Banner */}
              {subscription.is_trial && (
                <>
                  <div className={cn('bg-surface rounded-cards p-3 space-y-2', rail && 'lg:hidden')}>
                    <div className="flex items-center gap-2 text-foreground">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">Free trial</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {subscription.days_remaining > 0 
                        ? `${subscription.days_remaining} days remaining` 
                        : 'Trial ending today'}
                    </p>
                    <button
                      onClick={toBilling}
                      className="flex items-center gap-2 text-sm text-clay underline underline-offset-4 hover:text-foreground"
                    >
                      <CreditCard className="w-3 h-3" />
                      Upgrade now
                    </button>
                  </div>
                  {rail && (
                    <RailBadge
                      icon={Clock}
                      tone="blue"
                      label={`Free trial · ${subscription.days_remaining > 0 ? `${subscription.days_remaining} days remaining` : 'ending today'} · Upgrade`}
                      onClick={toBilling}
                    />
                  )}
                </>
              )}

              {/* Trial Expired Banner */}
              {subscription.status === 'expired' && (
                <>
                  <div className={cn('bg-brick-soft rounded-cards p-3 space-y-2', rail && 'lg:hidden')}>
                    <div className="flex items-center gap-2 text-brick">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">Trial expired</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Upgrade to continue creating jobs
                    </p>
                    <button
                      onClick={toBilling}
                      className="flex items-center gap-2 text-sm text-clay underline underline-offset-4 hover:text-foreground font-medium"
                    >
                      <CreditCard className="w-3 h-3" />
                      Upgrade now
                    </button>
                  </div>
                  {rail && <RailBadge icon={AlertTriangle} tone="red" label="Trial expired · Upgrade" onClick={toBilling} />}
                </>
              )}
            </>
          )}

          {/* Logout */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className={cn('w-full justify-start gap-3 text-muted-foreground hover:text-destructive', rail && 'lg:justify-center lg:px-0')}
            title={rail ? (isDemo ? 'Exit Demo' : 'Log Out') : undefined}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className={cn(rail && 'lg:sr-only')}>{isDemo ? 'Exit demo' : 'Log out'}</span>
          </Button>
        </div>
      </aside>

      {/* Desktop Spacer: the sidebar is fixed, so this holds its width in the page */}
      <div className={cn('hidden lg:block w-64 flex-shrink-0 transition-[width] duration-300', rail && 'lg:w-16')} />
    </>
  );
}

/**
 * One sidebar link. Collapsed to the rail it shows only its icon, names
 * itself by tooltip and to screen readers, and its count becomes a corner badge.
 */
function SideLink({ to, icon: Icon, label, alsoActiveOn, small, rail, onClick, badge, badgeTestId, testId }) {
  const { pathname } = useLocation();
  return (
    <NavLink
      to={to}
      onClick={onClick}
      title={rail ? label : undefined}
      className={({ isActive }) =>
        cn(
          // Lowercase clay links at rest; the current page is an inverse pill
          'relative flex items-center gap-3 px-3 rounded-pill lowercase transition-colors duration-200',
          small ? 'h-9 text-[15px]' : 'h-10 text-base',
          rail && 'lg:justify-center lg:px-0 lg:w-11 lg:mx-auto',
          isActive || alsoActiveOn?.some((p) => pathname.startsWith(p))
            ? 'bg-inverse text-inverse-foreground'
            : 'text-clay hover:bg-surface hover:text-foreground'
        )
      }
      data-testid={testId}
    >
      <Icon className={cn('shrink-0', small ? 'w-4 h-4' : 'w-[18px] h-[18px]')} />
      <span className={cn('flex-1 truncate', rail && 'lg:sr-only')}>{label}</span>
      <UnreadBadge
        count={badge}
        className={cn(rail && 'lg:absolute lg:-top-1 lg:-right-1 lg:w-4 lg:h-4 lg:text-[10px]')}
        data-testid={badgeTestId}
      />
    </NavLink>
  );
}

const RAIL_TONES = {
  amber: 'bg-ochre-soft border-transparent text-ochre',
  blue: 'bg-surface border-transparent text-foreground',
  red: 'bg-brick-soft border-transparent text-brick',
};

/** A plan or demo banner, shrunk to its icon for the collapsed rail. */
function RailBadge({ icon: Icon, tone, label, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn('hidden lg:flex w-full h-9 items-center justify-center rounded-pill border', RAIL_TONES[tone])}
    >
      <Icon className="w-4 h-4" />
    </Tag>
  );
}
